import Axios from 'axios';
import { action, observable, computed } from 'mobx';
import { Notification } from 'element-react';
import { API } from '@/config/api';

export interface PositionItem {
  autoMargin: boolean; // 是否自动最佳保证金
  availableCloseSize: string; // 可平仓数量（张）
  closeOrderSize: number; // 平仓挂单数量
  entryPrice: string; // 持仓成本价
  isolatedMargin: string; // 逐仓保证金
  leverage: number; // 杠杆倍数
  openOrderMarginFrozen: string; // 开仓订单冻结保证金
  positionSide: PositionSide; // 持仓方向
  positionSize: string; // 持仓数量
  positionType: PositionType; // 仓位类型
  realizedProfit: string; // 已实现盈亏
  state: number; // 仓位状态 0：无；1：使用中；2：强平中；3：冻结
  symbol: string; // 交易对

  // 本地计算
  floatProfit?: string; // 浮动盈亏
  floatProfitRate?: string; // 收益率
  adl?: number; // adl
  initialMargin: string; // 起始保证金
  liquidationPrice?: string; // 预估强平价
  maintMargin: string; // 维持保证金

  // 其他配置
  contractSize: number; // 面值
  quantityPrecision?: number; // 数量精度
  pricePrecision?: number; // 价格精度
  quoteCoinDisplayPrecision?: number; // 计价币种显示精度
}

export interface AdlItem {
  longQuantile: number;
  shortQuantile: number;
  symbol: string;
}

export default class Position {
  @observable positionList: PositionItem[];

  @observable allPositionList: PositionItem[];

  @observable adlList: AdlItem[];

  constructor() {
    this.positionList = [];
    this.allPositionList = [];
    this.adlList = [];
  }

  @action
  setPositionList(data: PositionItem[]) {
    this.positionList = data;
  }

  @action.bound
  async fetchPositionList(params?: { symbol: string }) {
    const result: any = await Axios.get(API.SPOT_POSITION_LIST, {
      params: {
        symbol: params?.symbol,
      },
    });

    if (Array.isArray(result)) {
      this.setPositionList(result);
    }
  }

  @action
  setAllPositionList(data: PositionItem[]) {
    this.allPositionList = data;
  }

  @action.bound
  async fetchAllPositionList() {
    // 项目仅支持 U 本位
    const uBasedPositionList = await Axios.get(API.SPOT_POSITION_LIST, {
      baseURL: '/f',
    });

    let result: any = [];

    if (Array.isArray(uBasedPositionList)) {
      result = result.concat(uBasedPositionList);
    }

    this.setAllPositionList(result);
  }

  @action.bound
  updatePositionListByRow(params: PositionItem) {
    const { symbol, positionSide } = params;
    // current underlying position
    const index = this.positionList.findIndex(item => item.symbol === symbol && item.positionSide === positionSide);

    const newList = this.positionList;
    if (index > -1) {
      const target = newList[index];
      const newItem = {
        ...target,
        ...params,
      };
      newList.splice(index, 1, newItem);
    } else {
      newList.unshift(params);
    }

    this.setPositionList(newList);

    // all position
    const indexAll = this.allPositionList.findIndex(
      item => item.symbol === symbol && item.positionSide === positionSide,
    );

    const newListAll = this.allPositionList;
    if (indexAll > -1) {
      const target = newListAll[indexAll];
      const newItem = {
        ...target,
        ...params,
      };
      newListAll.splice(indexAll, 1, newItem);
    } else {
      newListAll.unshift(params);
    }

    this.setAllPositionList(newListAll);
  }

  // 修改自动追加保证金
  @action.bound
  async fetchUpdateAutoMargin(params: { symbol: string; positionSide: string; autoMargin: boolean }) {
    const { symbol, positionSide, autoMargin } = params;

    try {
      return await Axios.post(API.SPOT_POSITION_AUTO_MARGIN, params)
        .then(() => {
          const newList = [...this.positionList];
          const index = newList.findIndex(
            (item: PositionItem) => symbol === item.symbol && positionSide === item.positionSide,
          );

          const position = this.positionList[index];
          position.autoMargin = autoMargin;

          newList.splice(index, 1, position);
          this.setPositionList(newList);
        })
        .catch(({ error }: any) => {
          Notification.error({
            message: error?.msg,
            offset: 60,
            duration: 2000,
          });
        });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  // 自动减仓等级
  @action
  setAdlList(data: AdlItem[]) {
    this.adlList = data;
  }

  @action.bound
  async fetchAdlList(params?: { symbol: string }) {
    const result: any = await Axios.get(API.SPOT_POSITION_ADL_LIST, {
      params: {
        symbol: params?.symbol,
      },
    });

    if (Array.isArray(result)) {
      this.setAdlList(result);
    }
  }

  @computed get adlMap() {
    const positionList = this.positionList;
    const adlList = this.adlList;
    const result: { [key: string]: any } = {};

    for (let i = 0; i < positionList.length; i += 1) {
      const item = positionList[i];
      const { symbol } = item;

      const adl = adlList.find(o => o.symbol === symbol);

      if (adl) {
        result[symbol] = adl;
      }
    }

    return result;
  }
}
